import './RatioBar.css';
import { colorForIngredientType } from '../utils/displayGroupColors';

/**
 * Stacked-bar visualization + legend + running total for user-ratios mode.
 *
 * Colors follow the 7 UI display categories (ingredient-display-groups.json),
 * so clove (bud) matches Spices & Aromatics with cinnamon / cardamom / etc.
 *
 * Props:
 *   - selectedIngredients: list of ingredient IDs in display order
 *   - ingredients: full ingredient list (for name + type lookup)
 *   - customPercentages: { [id]: number }
 *   - onResetToDefaults: () => void — invoked when the customer clicks "Reset
 *     to defaults". Parent re-fetches engine proportions and overwrites
 *     ``customPercentages``.
 *   - canResetToDefaults: boolean — only show the Reset button after the
 *     customer has edited a value. While they're still on the engine's
 *     suggestion, the button is meaningless (it'd be a no-op).
 */
function RatioBar({
  selectedIngredients,
  ingredients,
  customPercentages,
  onResetToDefaults,
  canResetToDefaults = false,
}) {
  const items = selectedIngredients.map((id) => {
    const ing = ingredients.find((i) => i.id === id) || { id, name: id, type: '' };
    const pct = Number(customPercentages[id]) || 0;
    return {
      id,
      name: (ing.name || id).replace(/_/g, ' '),
      type: ing.type,
      color: colorForIngredientType(ing.type),
      pct,
    };
  });

  const total = items.reduce((sum, item) => sum + item.pct, 0);
  // Tolerate IEEE float noise (matches backend ±1e-9), not user error.
  const isExact100 = Math.abs(total - 100) <= 1e-9;
  const totalDisplay = `${(Math.round(total * 10) / 10).toString()}%`;

  return (
    <div className="ratio-bar" role="group" aria-label="Ingredient proportions">
      <div className="ratio-bar__chart" aria-hidden="true">
        {items.map((item) => (
          <span
            key={item.id}
            className="ratio-bar__segment"
            style={{
              flexBasis: `${item.pct}%`,
              backgroundColor: item.color,
            }}
          />
        ))}
      </div>

      {items.length > 0 && (
        <ul className="ratio-bar__legend">
          {items.map((item) => (
            <li key={item.id} className="ratio-bar__legend-item">
              <span
                className="ratio-bar__legend-dot"
                style={{ backgroundColor: item.color }}
                aria-hidden="true"
              />
              <span className="ratio-bar__legend-name">{item.name}</span>
              <span className="ratio-bar__legend-pct">
                {`${(Math.round(item.pct * 10) / 10).toString()}%`}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div
        className={`ratio-bar__total${isExact100 ? '' : ' ratio-bar__total--off'}`}
      >
        <span className="ratio-bar__total-label">Total</span>
        <span className="ratio-bar__total-value">{totalDisplay}</span>
      </div>

      {typeof onResetToDefaults === 'function' && canResetToDefaults && (
        <button
          type="button"
          className="ratio-bar__reset"
          onClick={onResetToDefaults}
        >
          Reset to defaults
        </button>
      )}
    </div>
  );
}

export default RatioBar;
